import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '@/pages/gerenciar/direcao/mensagemBanco';
import CartaoTribo from './CartaoTribo';
import LinhaCampista from './LinhaCampista';
import BarraFiltrosTribos from './BarraFiltrosTribos';
import ModalSugerirTribos from './ModalSugerirTribos';
import TribosConfig from '@/pages/gerenciar/direcao/components/TribosConfig';
import {
  FILTROS_TRIBOS,
  cidadesResumo,
  type CampistaTribo,
  type DadosTribos,
  type FiltrosTribos,
} from './types';

interface Props {
  eventoId: number;
}

const CARD = 'rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';

// Configuração das tribos do acampamento, recolhida no topo de Campistas › Tribos.
function ConfigTribos({ eventoId }: { eventoId: number }) {
  const [aberto, setAberto] = useState(false);

  return (
    <div className={`${CARD} mb-3`}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="flex w-full cursor-pointer items-center gap-3 text-left"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-primary-50 text-[22px] text-primary-600">
          <i className="ri-tent-line" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-bold leading-tight text-foreground-950">
            Tribos deste acampamento{aberto ? '' : ' · ajustar'}
          </span>
          <span className="mt-0.5 block text-[13px] text-foreground-600">
            {aberto ? 'Toque para recolher' : 'Escolha as tribos, as cores e a ordem'}
          </span>
        </span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center text-foreground-400">
          <i
            className={`text-xl transition-transform ${aberto ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}
            aria-hidden
          />
        </span>
      </button>
      {aberto && (
        <div className="mt-3">
          <TribosConfig eventoId={eventoId} />
        </div>
      )}
    </div>
  );
}

export default function TribosCampistas({ eventoId }: Props) {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dados, setDados] = useState<DadosTribos>({ tribos: [], campistas: [] });
  const [selecionada, setSelecionada] = useState<number | 'sem' | null>(() =>
    typeof window !== 'undefined' && window.innerWidth >= 1024 ? 'sem' : null,
  );
  const [filtros, setFiltros] = useState<FiltrosTribos>(FILTROS_TRIBOS);
  const [modalSugerir, setModalSugerir] = useState(false);
  const [modalConfirmar, setModalConfirmar] = useState(false);
  const [incluirSuplentes, setIncluirSuplentes] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const carregar = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('tribos_montagem', { p_evento: eventoId });
      if (error) throw error;
      const bruto = (data ?? {}) as Partial<DadosTribos>;
      setDados({
        tribos: Array.isArray(bruto.tribos) ? bruto.tribos : [],
        campistas: Array.isArray(bruto.campistas) ? bruto.campistas : [],
      });
      setErro(null);
    } catch (e) {
      setErro(mensagemBanco(e));
    } finally {
      setCarregando(false);
    }
  }, [eventoId]);

  useEffect(() => {
    setCarregando(true);
    carregar();
  }, [carregar]);

  const total = dados.campistas.length;
  const comTribo = useMemo(
    () => dados.campistas.filter((c) => c.tribo_id !== null).length,
    [dados.campistas],
  );
  const semTriboTotal = total - comTribo;
  const sugestoes = useMemo(
    () => dados.campistas.filter((c) => c.status_tribo === 'sugerida').length,
    [dados.campistas],
  );

  const semTriboResumo = useMemo(() => {
    const lista = dados.campistas.filter((c) => c.tribo_id === null);
    const porCidade: Record<string, number> = {};
    lista.forEach((c) => {
      const k = c.cidade || 'Sem cidade';
      porCidade[k] = (porCidade[k] ?? 0) + 1;
    });
    const idades = lista
      .map((c) => c.idade)
      .filter((n): n is number => typeof n === 'number');
    return {
      total: lista.length,
      homens: lista.filter((c) => c.sexo === 'M').length,
      mulheres: lista.filter((c) => c.sexo === 'F').length,
      idadeMedia: idades.length
        ? Math.round(idades.reduce((a, b) => a + b, 0) / idades.length)
        : null,
      porCidade,
      sugestoes: lista.filter((c) => c.status_tribo === 'sugerida').length,
    };
  }, [dados.campistas]);

  const cidades = useMemo(() => {
    const set = new Set<string>();
    dados.campistas.forEach((c) => {
      set.add(c.cidade || 'Sem cidade');
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [dados.campistas]);

  const triboSelecionada =
    typeof selecionada === 'number'
      ? dados.tribos.find((t) => t.tribo_id === selecionada) ?? null
      : null;

  const pessoasDoGrupo = useMemo(() => {
    if (selecionada === null) return [];
    const base =
      selecionada === 'sem'
        ? dados.campistas.filter((c) => c.tribo_id === null)
        : dados.campistas.filter((c) => c.tribo_id === selecionada);
    const busca = filtros.busca.trim().toLowerCase();
    return base.filter((c) => {
      if (busca && !c.nome.toLowerCase().includes(busca)) return false;
      if (filtros.sexo && c.sexo !== filtros.sexo) return false;
      if (filtros.soSem && c.tribo_id !== null) return false;
      if (filtros.cidade && (c.cidade || 'Sem cidade') !== filtros.cidade) return false;
      return true;
    });
  }, [selecionada, dados.campistas, filtros]);

  const tituloLista =
    selecionada === 'sem' ? 'Sem tribo' : triboSelecionada?.nome ?? 'Tribos';

  function aoMudarFiltros(f: FiltrosTribos) {
    if (f.soSem && !filtros.soSem) setSelecionada('sem');
    setFiltros(f);
  }

  async function definirTribo(inscricaoId: number, triboId: number | null) {
    try {
      const { error } = await supabase.rpc('tribo_definir', {
        p_inscricao: inscricaoId,
        p_tribo: triboId,
      });
      if (error) throw error;
      await carregar();
      setToast(triboId === null ? 'Pessoa tirada da tribo.' : 'Tribo definida pela direção.');
    } catch (e) {
      setToast(mensagemBanco(e));
    }
  }

  async function sugerir() {
    setSalvando(true);
    try {
      const { data, error } = await supabase.rpc('tribos_sugerir', {
        p_evento: eventoId,
        p_incluir_suplentes: incluirSuplentes,
      });
      if (error) throw error;
      const n = Number((data as { sugeridos?: number } | null)?.sugeridos ?? 0);
      setModalSugerir(false);
      setIncluirSuplentes(false);
      await carregar();
      setToast(
        n === 0
          ? 'Ninguém ficou de fora: todos já têm tribo.'
          : `${n} ${n === 1 ? 'sugestão feita' : 'sugestões feitas'}.`,
      );
    } catch (e) {
      setToast(mensagemBanco(e));
    } finally {
      setSalvando(false);
    }
  }

  async function confirmarSugestoes() {
    setSalvando(true);
    try {
      const { data, error } = await supabase.rpc('tribos_confirmar_sugestoes', {
        p_evento: eventoId,
      });
      if (error) throw error;
      const n = Number((data as { confirmadas?: number } | null)?.confirmadas ?? 0);
      setModalConfirmar(false);
      await carregar();
      setToast(`${n} ${n === 1 ? 'tribo confirmada' : 'tribos confirmadas'}.`);
    } catch (e) {
      setToast(mensagemBanco(e));
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <div className="space-y-3">
        <div className="h-24 animate-pulse rounded-3xl bg-secondary-100" />
        <div className="h-12 animate-pulse rounded-2xl bg-secondary-100" />
        <div className="h-64 animate-pulse rounded-3xl bg-secondary-100" />
      </div>
    );
  }

  if (erro) {
    return (
      <div>
        <ConfigTribos eventoId={eventoId} />
        <div className={`${CARD} py-10 text-center`}>
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={() => {
            setCarregando(true);
            carregar();
          }}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
        </div>
      </div>
    );
  }

  if (dados.tribos.length === 0) {
    return (
      <div>
        <ConfigTribos eventoId={eventoId} />
        <div className={`${CARD} py-10 text-center`}>
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-100 text-accent-700">
            <i className="ri-group-2-line text-2xl" aria-hidden />
          </span>
          <p className="mt-3 text-[15px] font-semibold text-foreground-950">
            Este acampamento ainda não tem tribos escolhidas.
          </p>
          <p className="mt-1 text-[14px] text-foreground-600">
            Toque em “Tribos deste acampamento” para escolher.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ConfigTribos eventoId={eventoId} />
      <div className={`${CARD} mb-3`}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-foreground-950">Montagem das tribos</h2>
            <p className="mt-1 text-[13.5px] leading-relaxed text-foreground-600">
              <strong className="font-semibold text-foreground-900">{total}</strong>{' '}
              {total === 1 ? 'campista' : 'campistas'} · {comTribo} com tribo · {semTriboTotal} sem
              tribo · {sugestoes} {sugestoes === 1 ? 'sugestão' : 'sugestões'} a confirmar
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setModalSugerir(true)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-magic-line" aria-hidden />
              </span>
              Sugerir divisão
            </button>

            {sugestoes > 0 && (
              <button
                type="button"
                onClick={() => setModalConfirmar(true)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-300 bg-white px-4 py-2 text-[13px] font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-check-double-line" aria-hidden />
                </span>
                Confirmar todas as sugestões
              </button>
            )}
          </div>
        </div>
      </div>

      <BarraFiltrosTribos filtros={filtros} cidades={cidades} aoMudar={aoMudarFiltros} />

      <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start">
        <div
          className={`${
            selecionada !== null ? 'hidden lg:block' : 'block'
          } w-full space-y-2 lg:w-[330px] lg:shrink-0`}
        >
          <CartaoTribo
            nome="Sem tribo"
            cor={null}
            total={semTriboResumo.total}
            homens={semTriboResumo.homens}
            mulheres={semTriboResumo.mulheres}
            idadeMedia={semTriboResumo.idadeMedia}
            cidades={cidadesResumo(semTriboResumo.porCidade)}
            sugestoes={semTriboResumo.sugestoes}
            ativo={selecionada === 'sem'}
            aoSelecionar={() => setSelecionada('sem')}
          />

          {dados.tribos.map((t) => {
            const aConfirmar = dados.campistas.filter(
              (c) => c.tribo_id === t.tribo_id && c.status_tribo === 'sugerida',
            ).length;
            return (
              <CartaoTribo
                key={t.tribo_id}
                nome={t.nome}
                cor={t.cor}
                total={Number(t.total) || 0}
                homens={Number(t.homens) || 0}
                mulheres={Number(t.mulheres) || 0}
                idadeMedia={
                  typeof t.idade_media === 'number' && Number.isFinite(t.idade_media)
                    ? Math.round(t.idade_media)
                    : null
                }
                cidades={cidadesResumo(t.por_cidade)}
                sugestoes={aConfirmar}
                ativo={selecionada === t.tribo_id}
                aoSelecionar={() => setSelecionada(t.tribo_id)}
              />
            );
          })}
        </div>

        <div
          className={`${selecionada !== null ? 'block' : 'hidden lg:block'} min-w-0 flex-1`}
        >
          <button
            type="button"
            onClick={() => setSelecionada(null)}
            className="mb-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900 lg:hidden"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line" aria-hidden />
            </span>
            Voltar às tribos
          </button>

          <div className={CARD}>
            {selecionada === null ? (
              <div className="py-12 text-center">
                <p className="text-[14px] text-foreground-600">
                  Escolha uma tribo ao lado para ver e mover as pessoas.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{
                        backgroundColor:
                          triboSelecionada?.cor ?? 'transparent',
                        border: triboSelecionada ? 'none' : '2px solid #94A3B8',
                      }}
                      aria-hidden
                    />
                    <h3 className="truncate text-[15px] font-bold text-foreground-950">
                      {tituloLista}
                    </h3>
                  </div>
                  <span className="shrink-0 text-[13px] font-medium text-foreground-600">
                    {pessoasDoGrupo.length}{' '}
                    {pessoasDoGrupo.length === 1 ? 'pessoa' : 'pessoas'}
                  </span>
                </div>

                {pessoasDoGrupo.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-[14px] text-foreground-600">
                      Ninguém aqui com esses filtros.
                    </p>
                  </div>
                ) : (
                  <div className="mt-2">
                    {pessoasDoGrupo.map((c: CampistaTribo) => (
                      <LinhaCampista
                        key={c.inscricao_id}
                        campista={c}
                        tribos={dados.tribos}
                        aoMover={(id, triboId) => definirTribo(id, triboId)}
                        aoConfirmar={(p) => {
                          if (p.tribo_id !== null) definirTribo(p.inscricao_id, p.tribo_id);
                        }}
                        aoTirar={(p) => definirTribo(p.inscricao_id, null)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ModalSugerirTribos
        aberto={modalSugerir}
        salvando={salvando}
        incluirSuplentes={incluirSuplentes}
        aoMudarSuplentes={setIncluirSuplentes}
        aoSugerir={sugerir}
        aoCancelar={() => {
          setModalSugerir(false);
          setIncluirSuplentes(false);
        }}
      />

      {modalConfirmar && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/30 sm:items-center sm:p-6">
          <div className="w-full max-w-sm rounded-t-3xl bg-white p-6 sm:rounded-3xl">
            <h3 className="text-[17px] font-bold text-foreground-950">
              Confirmar as {sugestoes} sugestões?
            </h3>
            <p className="mt-2 text-[14px] leading-relaxed text-foreground-700">
              A direção pode mudar depois, pessoa por pessoa.
            </p>
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={() => setModalConfirmar(false)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-white px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={confirmarSugestoes}
                disabled={salvando}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {salvando ? 'Confirmando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-2xl bg-foreground-950 px-4 py-3 text-center text-[14px] text-white">
          {toast}
        </div>
      )}
    </div>
  );
}